<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import PostSaleIndexDetail from '../components/PostSaleIndexDetail.vue';
import PostSaleIndexHeader from '../components/PostSaleIndexHeader.vue';
import PostSaleIndexSections from '../components/PostSaleIndexSections.vue';
import PostSaleCasesTableActionOverlays from '../components/PostSaleCasesTableActionOverlays.vue';
import { postSaleAgendaLocation } from '../composables/postSaleAgendaNavigation';
import {
  canManagePostSale,
  postSaleInitialSection,
} from '../composables/postSaleInitialSection';
import { usePostSaleExternalAction } from '../composables/usePostSaleExternalAction';
import { usePostSaleCases } from '../composables/usePostSaleCases';
import { usePostSaleManagement } from '../composables/usePostSaleManagement';
import { usePostSaleTableActions } from '../composables/usePostSaleTableActions';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentAccount = useMapGetter('getCurrentAccount');
const currentUser = useMapGetter('getCurrentUser');
const canManage = computed(() =>
  canManagePostSale(currentAccount.value?.permissions || [])
);
const initialSection = computed(() =>
  postSaleInitialSection(currentAccount.value?.permissions || [])
);
const postSale = usePostSaleCases({
  route,
  router,
  t,
  notify: useAlert,
  initialSection,
});
const management = usePostSaleManagement({
  activeTab: postSale.managementTab,
  dashboard: postSale.dashboard,
  notify: useAlert,
  t,
});
const externalAction = usePostSaleExternalAction({ t, notify: useAlert });
const tableActions = usePostSaleTableActions({
  accountId: route.params.accountId,
  notify: useAlert,
  refresh: postSale.refresh,
  router,
  t,
});

const sectionTabs = computed(() => [
  ...(canManage.value
    ? [
        {
          id: 'gestao',
          label: t('POST_SALE.SECTIONS.MANAGEMENT'),
          icon: 'i-lucide-house',
        },
      ]
    : []),
  {
    id: 'prioridades',
    label: t('POST_SALE.SECTIONS.PRIORITIES'),
    icon: 'i-lucide-calendar-days',
  },
  {
    id: 'vendas',
    label: t('POST_SALE.SECTIONS.SALES'),
    icon: 'i-lucide-building-2',
  },
]);

const openCorrection = postSaleCase =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_devolucao',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: postSaleCase.property_transaction_id,
    },
  });
const openNucleoRequest = postSaleCase =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_pedidos_servico',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: postSaleCase.property_transaction_id,
    },
  });
const openAgendaAction = context => {
  if (context.action?.kind === 'external')
    return externalAction.request(context);
  return router.push(
    postSaleAgendaLocation({
      accountId: route.params.accountId,
      action: context.action,
      postSaleCase: context.postSaleCase,
      query: postSale.queryState(),
    })
  );
};
const refresh = () => Promise.all([postSale.refresh(), management.refresh()]);
const viewFilteredManagement = ({ type, value }) =>
  postSale.openFilteredList({
    ...postSale.filters.value,
    payment_method: type === 'payments' ? value : '',
    laudemio_type: type === 'laudemiums' ? value : '',
    deadline: type === 'status' ? value : '',
  });
const saveNote = payload =>
  postSale.save(postSale.selectedCase.value.id, payload);
const deleteNote = note =>
  postSale.remove(postSale.selectedCase.value.id, note);
const openTopic = topic =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_analise',
    params: {
      accountId: route.params.accountId,
      postSaleCaseId: postSale.selectedCase.value.id,
    },
    query: { cardId: topic.id },
  });
const openAnalysis = () =>
  router.push({
    name: 'operacao_imobiliaria_pos_venda_analise',
    params: {
      accountId: route.params.accountId,
      postSaleCaseId: postSale.selectedCase.value.id,
    },
  });
const openTableAction = context => tableActions.open(context);
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full overflow-x-hidden bg-lais-bg-soft"
  >
    <PostSaleIndexHeader
      :loading="postSale.loading.value || postSale.loadingDashboard.value"
      @open-notification="postSale.openCase({ id: $event.post_sale_case_id })"
      @refresh="refresh"
    />
    <main class="flex w-full min-w-0 flex-col">
      <PostSaleIndexSections
        :management="management"
        :post-sale="postSale"
        :section-tabs="sectionTabs"
        @change-section="postSale.selectSection"
        @change-management-tab="postSale.setManagementTab"
        @open-agenda-action="openAgendaAction"
        @open-case="postSale.openCase({ id: $event })"
        @refresh-queue="management.loadDashboard"
        @set-period="management.setPeriod"
        @table-action="openTableAction"
        @toggle-queue="management.updateQueue"
        @view-filtered="viewFilteredManagement"
      />
    </main>
    <PostSaleIndexDetail
      :post-sale="postSale"
      @assign="postSale.assign"
      @change-status="postSale.openTransition(postSale.selectedCase.value.id)"
      @close-note-form="postSale.closeForm"
      @close="postSale.closeCase"
      @create-note="postSale.openCreate"
      @delete-note="deleteNote"
      @edit-note="postSale.openEdit"
      @edit-topic="openTopic"
      @open-analysis="openAnalysis"
      @request-correction="openCorrection(postSale.selectedCase.value)"
      @request-nucleo="openNucleoRequest(postSale.selectedCase.value)"
      @save-note="saveNote"
      @update-responsible="postSale.responsibleUserId.value = $event"
    />
    <PostSaleCasesTableActionOverlays
      :action="tableActions.action.value"
      :agents="tableActions.agents.value"
      :assigning="tableActions.assigning.value"
      :cancelling="tableActions.cancelling.value"
      :case-data="tableActions.caseData.value"
      :loading-agents="tableActions.loadingAgents.value"
      @assign="tableActions.assign"
      @cancel="tableActions.cancel"
      @close="tableActions.close"
    />
  </LaisGamificationSurface>
</template>
